import type { RouteLocation } from 'vue-router'
import { useStorage } from '@vueuse/core'
import {
  getAuthStatus,
  requestLogin,
  requestLogout,
  requestRegister,
  requestSetNewPassword,
  requestStartPasswordReset,
  setApiRootUrl,
} from '../service'

const LOGIN_PATH = '/sign-in'
const DEFAULT_PATH = '/'

// Paths served by Django (not Nuxt). After login, a `next=` pointing here
// must use full-page navigation so the browser attaches the fresh session
// cookie; Nuxt's internal router has no route for these and would render
// its catch-all 404. Extend if we add more Django-backed views that redirect
// unauthenticated users through `/sign-in`.
// Deliberately no trailing slash on ``/graphql`` — Django registers the route
// at exactly that path (``path("graphql", …)`` in ``sleuth/urls.py``), with
// ``/graphql/batch`` as a separate route. Explicit both-forms check in
// ``isBackendPath`` avoids accidentally matching a hypothetical
// ``/graphql-admin``.
const BACKEND_PATH_PREFIXES = ['/api/', '/_api/', '/admin/', '/static/']

// Explicit deny-list for post-auth ``next=`` targets. Landing a freshly-
// authenticated user on an OAuth authorize URL (via ``next=/api/oauth/
// authorize/?client_id=...&redirect_uri=...``) would let an attacker who
// registered a DCR client hijack consent: the user sees "Authorize {app}"
// for an app they didn't intend to connect. Only the consent-screen click
// defends, and social engineering beats that. Block this specific path (and
// anything under it) regardless of whether the rest of the path is
// same-origin.
const DISALLOWED_POST_AUTH_REDIRECT_PREFIXES = ['/api/oauth/authorize']

function isBackendPath(path: string): boolean {
  if (BACKEND_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))) return true
  // Exact match on ``/graphql``, and anything under ``/graphql/`` (e.g.
  // ``/graphql/batch``), without matching ``/graphql-admin``.
  if (path === '/graphql' || path.startsWith('/graphql/')) return true
  return false
}

function isDisallowedPostAuthRedirect(path: string): boolean {
  return DISALLOWED_POST_AUTH_REDIRECT_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`),
  )
}

function getRedirectPathFromQuery(route: RouteLocation) {
  return route.query.next ? decodeURIComponent(route.query.next.toString()) : null
}

// Shared post-auth redirect. Nuxt's router can't navigate to Django-backed
// URLs (e.g. /api/oauth/authorize/) — an internal navigateTo would render the
// Nuxt 404 page with the backend URL stuck in the address bar. For backend
// paths we force a full-page navigation so the browser attaches the fresh
// session cookie to the next request. All three post-auth entry points
// (`login`, the watcher, SSO/register completion) must go through this.
//
// Security: fail closed on any non-same-origin value. An attacker-controlled
// ``?next=https://evil.com`` could otherwise ride a completed login into an
// open redirect via ``navigateTo``. We require the redirect to begin with
// ``/`` AND to not begin with ``//`` (protocol-relative URL); everything else
// falls back to the default path.
async function redirectAfterAuth(redirectPath: string) {
  if (!redirectPath.startsWith('/') || redirectPath.startsWith('//')) {
    await navigateTo(DEFAULT_PATH)
    return
  }
  if (isDisallowedPostAuthRedirect(redirectPath)) {
    // See comment on ``DISALLOWED_POST_AUTH_REDIRECT_PREFIXES``. Fail to
    // DEFAULT_PATH rather than the attacker-requested URL.
    await navigateTo(DEFAULT_PATH)
    return
  }
  if (isBackendPath(redirectPath)) {
    window.location.href = redirectPath
    return
  }
  await navigateTo(redirectPath)
}

async function startPasswordReset(email: string) {
  const [success, errors] = await requestStartPasswordReset(email)

  if (success) {
    await navigateTo({ name: 'reset-password-request-success' })
    return null
  } else {
    return errors as Record<string, string[]>
  }
}

async function setNewPassword(uid: string, password: string, passwordRepeated: string) {
  const [success, errors] = await requestSetNewPassword(uid, password, passwordRepeated)

  if (success) {
    await navigateTo({ name: 'reset-password-reset-success' })
    return null
  } else {
    return errors as Record<string, string[]>
  }
}

export default defineNuxtPlugin(async (nuxtApp) => {
  // Skip plugin when rendering error page
  if (nuxtApp.payload.error) return {}

  setApiRootUrl('/_api')

  const isAuthenticated = useStorage('isAuthenticated', false)
  const currentRoute = useRoute()

  function setIsAuthenticated(newStatus: boolean) {
    isAuthenticated.value = newStatus
  }

  async function login(username: string, password: string) {
    const [success, error] = await requestLogin(username, password)

    if (success) {
      setIsAuthenticated(true)
      const redirectPath = getRedirectPathFromQuery(currentRoute)
      if (redirectPath) {
        await redirectAfterAuth(redirectPath)
        return null
      }
      await navigateTo({ path: DEFAULT_PATH, query: currentRoute.query })
    }

    return error ? String(error) : null
  }

  async function logoutAndClearLocalStorage() {
    localStorage.removeItem('last-workspace')
    const [success, error] = await requestLogout()

    if (success) setIsAuthenticated(false)

    return error ? String(error) : null
  }

  async function register(
    organizationName: string,
    email: string,
    password: string,
    invitationCode?: string,
    betaAccessCode?: string,
  ) {
    const [success, errors] = await requestRegister(organizationName, email, password, invitationCode, betaAccessCode)

    if (success) {
      setIsAuthenticated(true)

      // New org creation (no invitation) → onboarding wizard
      // Invitation signup → homepage
      await navigateTo(invitationCode ? '/' : '/onboarding-wizard')
      return null
    } else {
      return errors as Record<string, string[]>
    }
  }

  const [currentAuthStatus] = await getAuthStatus()
  setIsAuthenticated(currentAuthStatus)

  addRouteMiddleware(
    'auth',
    (to) => {
      if (to.meta.auth !== false && !isAuthenticated.value) {
        if (to.meta.anonymousAllowed) return

        const query = to.path === DEFAULT_PATH ? {} : { next: encodeURIComponent(to.fullPath) }
        return { path: LOGIN_PATH, query }
      } else if (to.path === LOGIN_PATH && isAuthenticated.value) {
        return { path: DEFAULT_PATH, query: to.query }
      } else if (isAuthenticated.value) {
        const redirectPath = getRedirectPathFromQuery(to)
        if (redirectPath) {
          // Skip internal redirect for Django-backed paths — the login()
          // function handles those via window.location.href so the browser
          // sends the session cookie to nginx → Django. Returning the path
          // here would try to route it through Nuxt and 404.
          if (isBackendPath(redirectPath)) return
          return redirectPath
        }
      }
    },
    { global: true },
  )

  watch(isAuthenticated, async (newStatus) => {
    if (newStatus === false) {
      const cachePlugin = useGqlCache()
      cachePlugin.clearCache()

      if (currentRoute.meta.auth !== false) {
        const query = { next: encodeURIComponent(currentRoute.fullPath) }
        await navigateTo({ path: LOGIN_PATH, query })
      }
    } else {
      const redirectPath = getRedirectPathFromQuery(currentRoute)
      if (currentRoute.path === LOGIN_PATH && redirectPath) await redirectAfterAuth(redirectPath)
    }
  })

  return {
    provide: {
      auth: { login, logoutAndClearLocalStorage, register, startPasswordReset, setNewPassword, isAuthenticated },
    },
  }
})
